import { css } from '@emotion/css';
import { Table } from 'antd';
import TileButton, { tileDataProp } from 'components/atoms/TileButton';
import ErrorBox from 'components/molecules/ErrorBox/ErrorBox';
import CopyToClipBoard from 'components/molecules/MicroIntractions/CopyTOClipBoard';
import DownloadExcel from 'components/molecules/MicroIntractions/DownloadExcel';
import { subHeaderClass } from 'components/template/GrowthAnalysis/ExpandedView';
import { UserContext } from 'contexts/ConfigContext';
import { FilterContextData } from 'contexts/FilterContext';
import { useContainerWidth } from 'hooks/general/useContainerWidth';
import { useFetchProfitPool } from 'hooks/growthAnalysis/useFetchProfitPoolChart';
import { useFetchSalesAttribute } from 'hooks/growthAnalysis/useFetchSalesAttribute';
import _ from 'lodash';
import { salesAttributionChartDownload } from 'parser/graphToExcelDownloadParser';
import React, { FC, useContext, useEffect, useMemo, useRef, useState } from 'react';
import tw from 'twin.macro';
import { getSalesAttributionColumns } from 'utils/getSalesAttributionColumns';
import { getAdditionalDataOnCopyClick, getConfig } from 'utils/helperMethods';
import { trackEventMixpanel } from 'utils/mixpanel';
import { GROWTH_ANALYSIS_SALES_CHART_FACTOR_CLICKED } from 'utils/mixpanelEventNames';
const buttonList = [
    { key: 'volume_sales', label: 'Volume' },
    { key: 'value_sales', label: 'Value' },
];
export const tableStyle = css`
    .ant-table-thead .ant-table-cell {
        ${tw`bg-greyBackground text-fontColor !font-semibold`}
    }
    .ant-table-thead > tr {
        height: 40px;
    }

    .ant-table-thead
        > tr
        > th:not(:last-child):not(.ant-table-selection-column):not(.ant-table-row-expand-icon-cell):not(
            [colspan]
        ):before {
        background: none;
    }

    .ant-table-tbody > tr.ant-table-row:hover > td {
        background: unset;
    }
`;

const attributionChartHeader = 'Sales Attribution Chart vs YA :';
const profitPoolRetailerHeader = 'Profit Waterfall Chart: Retailer';
const profitPoolOwnHeader = 'Profit Waterfall Chart: Own';

interface Props {
    onClick?: Function;
    selected?: string;
    showTileButtons?: boolean;
    index: number;
    stringifiedFilter: string;
    volTile: tileDataProp;
    loading: boolean;
    expandedItem: any;
}

//SALES ATTRIBUTION CHART COMPONENT
const SalesAttributionChart: FC<Props> = ({
    index,
    onClick,
    selected,
    showTileButtons = false,
    stringifiedFilter,
    volTile,
    loading,
    expandedItem,
}) => {
    const containerWidth = useContainerWidth({ containerId: 'saleAttributionTable', buffer: 16 * 2 + 6 }); //buffer calculation (16*2 gives the padding and 6 subtracting borders of 1 px)
    const [attributeTile, setAttributeTile] = useState(buttonList[0]);
    const { appConfig } = useContext(UserContext);
    const growthAnalysisConfig = getConfig('growthAnalysis', appConfig);
    const { salesAttributionChart: salesAttributionChartConfig } = growthAnalysisConfig;
    const salesAttributionTableRef = useRef<any>(null);
    const salesAttributionChartRef = useRef<any>();
    const { timePeriodSelection, allCheckedAttribute, timeSelectionLabel } = useContext(FilterContextData);

    const {
        data: featureData,
        loading: featureLoading,
        error: featureError,
        extra,
        fetchAttributeSales: fetchFeatureData,
    } = useFetchSalesAttribute();
    const {
        data: profitPoolData,
        loading: profitPoolDataLoading,
        error: profitPoolError,
        fetchProfitPool,
        extra: profitPoolExtra,
    } = useFetchProfitPool();

    //checks if current dropdown is profit
    const isProfit = volTile.key === 'profit';

    useEffect(() => {
        const variables = {
            contributionType: volTile?.dataKey?.toLowerCase(),
            filter: stringifiedFilter,
            timePeriod: String(timePeriodSelection).toLowerCase(),
            aggPeriod:
                String(timeSelectionLabel)?.toLowerCase() === 'custom date' ||
                String(timeSelectionLabel)?.toLowerCase() === 'ytd'
                    ? ''
                    : String(timeSelectionLabel)?.toUpperCase(),
        };
        if (!loading) {
            //for profit, get profit pool data
            if (isProfit) {
                fetchProfitPool({ variables });
            } else fetchFeatureData({ variables });
        }
    }, [volTile, stringifiedFilter, timePeriodSelection, loading]);
    const profitPoolData1 = profitPoolData?.data1;
    const profitPoolData2 = profitPoolData?.data2;
    const profitPoolExtra1 = profitPoolExtra?.extraData1;
    const profitPoolExtra2 = profitPoolExtra?.extraData2;

    const attributionExcelData = useMemo(() => {
        return salesAttributionChartDownload(extra?.headerTitle, featureData, volTile?.label);
    }, [featureData, extra?.headerTitle]);

    return (
        <div ref={salesAttributionChartRef}>
            {/* show attribution chart for volume,value and units */}
            {!isProfit ? (
                <div className="flex justify-between items-center gap-2 px-4 mb-4">
                    <p className={`${subHeaderClass}`}>
                        {attributionChartHeader} {expandedItem?.attributeExId}
                    </p>
                    <div className="flex items-center justify-end gap-2">
                        <CopyToClipBoard refOfComponent={salesAttributionChartRef} />
                        <DownloadExcel
                            title={'AttributionChart'}
                            excelData={attributionExcelData.data}
                            headers={attributionExcelData.headers}
                        />
                    </div>
                    {showTileButtons && (
                        <TileButton
                            data={buttonList}
                            activeKey={attributeTile.key}
                            onClick={(item: any) => setAttributeTile(item)}
                        />
                    )}
                </div>
            ) : null}

            {/* sales attribution chart */}
            {/* show attribution chart for volume,value and units */}
            {!isProfit ? (
                <Table
                    ref={salesAttributionTableRef}
                    id="saleAttributionTable"
                    className={tableStyle}
                    pagination={false}
                    loading={featureLoading || loading}
                    locale={{
                        emptyText: featureError ? <ErrorBox msg={String(featureError)} /> : '',
                    }}
                    columns={getSalesAttributionColumns(
                        [salesAttributionChartConfig?.colorList, salesAttributionChartConfig?.profitPoolColor],
                        volTile,
                        featureData,
                        'attributionChart',
                        {
                            max: extra?.maxValue,
                            min: extra?.minValue,
                            headerTitle: extra?.headerTitle,

                            barLength: extra?.length,
                            onClick: (item: any) => {
                                trackEventMixpanel(GROWTH_ANALYSIS_SALES_CHART_FACTOR_CLICKED, {
                                    factorName: _.startCase(item),
                                });
                                if (onClick) onClick(item);
                            },
                            selected: selected,
                            width: containerWidth,
                            attributeTypeIndex: index,
                            config: appConfig,
                        },
                    )}
                    dataSource={featureData}
                    size="small"
                    scroll={{ x: 1000 }}
                    bordered={true}
                />
            ) : (
                // profit pool chart
                <div>
                    {/* RETAILER PROFIT POOL */}
                    <p className={`${subHeaderClass} pl-4 mb-4`}>{profitPoolRetailerHeader}</p>
                    <Table
                        ref={salesAttributionTableRef}
                        id="profitPoolRetailer"
                        className={tableStyle}
                        pagination={false}
                        loading={profitPoolDataLoading || loading}
                        locale={{
                            emptyText: profitPoolError ? <ErrorBox msg={String(profitPoolError)} /> : '',
                        }}
                        columns={getSalesAttributionColumns(
                            [salesAttributionChartConfig?.colorList, salesAttributionChartConfig?.profitPoolColor],
                            volTile,
                            profitPoolData1,
                            'profitPoolRetailer',
                            {
                                max: profitPoolExtra1?.maxValue,
                                min: profitPoolExtra1?.minValue,
                                headerTitle: profitPoolExtra1?.headerTitle,

                                barLength: profitPoolExtra1?.length,
                                onClick: undefined,
                                selected: selected,
                                width: containerWidth,
                                attributeTypeIndex: index,
                            },
                        )}
                        dataSource={profitPoolData1}
                        size="small"
                        scroll={{ x: 1000 }}
                        bordered={true}
                    />
                    {/* OWN PROFIT POOL */}
                    <p className={`${subHeaderClass} pl-4 my-4`}>{profitPoolOwnHeader}</p>
                    <Table
                        ref={salesAttributionTableRef}
                        id="profitPoolOwn"
                        className={tableStyle}
                        pagination={false}
                        loading={profitPoolDataLoading || loading}
                        locale={{
                            emptyText: profitPoolError ? <ErrorBox msg={String(profitPoolError)} /> : '',
                        }}
                        columns={getSalesAttributionColumns(
                            [salesAttributionChartConfig?.colorList, salesAttributionChartConfig?.profitPoolColor],
                            volTile,
                            profitPoolData2,
                            'profitPoolOwn',
                            {
                                max: profitPoolExtra2?.maxValue,
                                min: profitPoolExtra2?.minValue,
                                headerTitle: profitPoolExtra2?.headerTitle,

                                barLength: profitPoolExtra2?.length,
                                onClick: undefined,
                                selected: selected,
                                width: containerWidth,
                                attributeTypeIndex: index,
                            },
                        )}
                        dataSource={profitPoolData2}
                        size="small"
                        scroll={{ x: 1000 }}
                        bordered={true}
                    />
                </div>
            )}
        </div>
    );
};

export default SalesAttributionChart;
